import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";
import SectionHeader from "../components/SectionHeader";
import CTABand from "../components/CTABand";
import { COMPANY, VALUES, HOW_WE_WORK, IMAGES } from "../data/content";

const About = () => (
  <main data-testid="about-page">
    <PageHeader
      eyebrow="About Us"
      title={COMPANY.name}
      description="An Indian software development, AI and IT solutions company building secure, scalable and future-ready digital products for clients in India and internationally."
    />

    {/* Company overview */}
    <section className="bg-white py-20 lg:py-24" data-testid="about-overview">
      <div className="container-site grid items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <SectionHeader
            eyebrow="Company Overview"
            title="Engineering with purpose"
            description="Vaskion Technologies Private Limited is a software development, AI and IT solutions company. We work with organizations to design, build and operate digital products and platforms — combining modern engineering practices with a clear focus on business outcomes. Our work spans custom software, enterprise systems, applied AI, cloud, data and automation."
          />
        </Reveal>
        <Reveal delay={150}>
          <img
            src={IMAGES.architecture}
            alt="Rhythmic white architectural structure"
            className="v-clip aspect-[4/3] w-full object-cover"
            loading="lazy"
            data-testid="about-overview-image"
          />
        </Reveal>
      </div>
    </section>

    {/* Mission & Vision */}
    <section className="border-y border-border bg-brand-surface py-20 lg:py-24" data-testid="about-mission-vision">
      <div className="container-site grid gap-6 md:grid-cols-2">
        <Reveal>
          <div className="h-full border border-border bg-white p-10 shadow-sm">
            <div className="mb-4 h-1 w-12 bg-brand-red" aria-hidden="true" />
            <p className="eyebrow">Mission</p>
            <p className="mt-4 text-lg leading-relaxed text-brand-graphite" data-testid="about-mission-text">
              To help businesses innovate and grow by delivering secure, reliable and thoughtfully
              engineered digital solutions.
            </p>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="h-full border border-border bg-white p-10 shadow-sm">
            <div className="mb-4 h-1 w-12 bg-brand-red" aria-hidden="true" />
            <p className="eyebrow">Vision</p>
            <p className="mt-4 text-lg leading-relaxed text-brand-graphite" data-testid="about-vision-text">
              To become a trusted technology partner for organizations building the next generation
              of digital products and services.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Core values */}
    <section className="bg-white py-20 lg:py-24" data-testid="about-values">
      <div className="container-site">
        <SectionHeader
          eyebrow="What Guides Us"
          title="Core Values"
          description="The principles that shape how we work with clients and with each other."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {VALUES.map((value, i) => (
            <Reveal key={value.title} delay={(i % 3) * 80}>
              <div
                className="h-full border border-border bg-white p-8 shadow-sm transition-colors duration-200 hover:border-brand-red"
                data-testid={`value-card-${i}`}
              >
                <h3 className="font-heading text-lg font-semibold">{value.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-grey">{value.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* How we work */}
    <section className="border-y border-border bg-brand-surface py-20 lg:py-24" data-testid="about-how-we-work">
      <div className="container-site">
        <SectionHeader
          eyebrow="Our Process"
          title="How We Work"
          description="A transparent, iterative way of working that keeps you informed and in control."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {HOW_WE_WORK.map((phase, i) => (
            <Reveal key={phase.step} delay={i * 90}>
              <div className="h-full border-t-2 border-brand-red bg-white p-8 shadow-sm" data-testid={`how-we-work-step-${i}`}>
                <span className="font-heading text-sm font-semibold text-brand-red">{phase.step}</span>
                <h3 className="mt-3 text-lg font-semibold">{phase.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-grey">{phase.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Engineering approach */}
    <section className="bg-white py-20 lg:py-24" data-testid="about-engineering-approach">
      <div className="container-site grid items-start gap-14 lg:grid-cols-2">
        <SectionHeader
          eyebrow="Engineering Approach"
          title="Disciplined by default"
          description="We believe good software comes from good habits. Our engineering approach combines modern architectures, automated testing, code review, security-first thinking and clear documentation — so the systems we deliver remain maintainable long after launch."
        />
        <ul className="space-y-5">
          {[
            "Security and privacy considerations from the first design discussion",
            "Modular, well-documented codebases built for long-term maintenance",
            "Iterative delivery with regular, visible progress",
            "Automated testing and review as standard practice, not an extra",
            "Pragmatic technology choices matched to your context and team",
          ].map((point, i) => (
            <Reveal key={point} delay={i * 60}>
              <li className="flex items-start gap-4 border-b border-border pb-5 text-base leading-relaxed text-brand-grey" data-testid={`engineering-approach-point-${i}`}>
                <span className="mt-2 h-2 w-2 shrink-0 bg-brand-red" aria-hidden="true" />
                {point}
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>

    <CTABand />
  </main>
);

export default About;
